<script setup lang="ts">
import TelefonField from '@/components/telefon-field.vue'
import PlzOrtSelect from '@/components/plz-ort-select.vue'
import LandSelect from '@/components/land-select.vue'
import AnredeSelect from '@/components/anrede-select.vue'
import { Adresse, AdresseCollection } from '@/models/Adresse'
import EigentuemerschaftSelect from '@/components/eigentuemerschaft-select.vue'
import KpTextfield from '@/components/kp-textfield.vue'
import { completeStreetString, debounce } from '@/helpers/tools'
import { formRules } from '@/helpers/validators'
import type { Land } from '@/models/Land'
import { useGlobals } from '@/helpers/globals'
import { ref } from 'vue'

const {
    adresse = new Adresse(),
    isProfil = false,
    gesuchContext = false,
} = defineProps<{
    adresse?: Adresse
    isProfil?: boolean
    loading?: boolean
    isNew?: boolean
    gesuchContext?: boolean
}>()

const modStreet = ref(completeStreetString)
const valid = ref(false)
const contact = ref(null)
const contactLoading = ref(false)
const contactCollection = new AdresseCollection()
const customer = ref(useGlobals().customer)

// watch(value, (val, oldVal) => {
//     if (val && !oldVal && isNew && $refs.organisationCardForm) {
//         $refs.organisationCardForm.resetValidation()
//     }
// })

async function loadAnsprechsPerson(query: string) {
    await contactCollection.query({ query: query, gesuchId: -1 })
}

async function searchAnsprechPerson(value: string) {
    if (!value || value.length < 3) {
        return
    }
    debounce(loadAnsprechsPerson, 300)(value)
}

function onLandChange(land: Land) {
    adresse.set('laendercode', land.laendercode)
    adresse.set('land', land.name)
}

function contactSelected(contact: Adresse | null) {
    if (contact) {
        adresse.set('anrede', contact.anrede)
        adresse.set('vorname', contact.vorname)
        adresse.set('nachname', contact.nachname)
        adresse.set('tel_1', contact.tel_1)
        adresse.set('tel_2', contact.tel_2)
        adresse.set('email', contact.email)
    }
}
</script>

<template>
    <div v-if="adresse">
        <v-form
            ref="organisationCardForm"
            v-model:model-value="valid"
            autocomplete="new-password"
            @update:model-value="$emit('validchange', valid)"
        >
            <v-card-title text-color="white">
                <v-icon left>fas fa-building</v-icon>
                {{ adresse.organisation_name }}
            </v-card-title>
            <v-card-text>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.organisation_name"
                            @update:model-value="(value) => adresse.set('organisation_name', value)"
                            dense
                            :label="$t('name') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(100)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.adresszusatz"
                            @update:model-value="(value) => adresse.set('adresszusatz', value)"
                            dense
                            :label="$t('adresszusatz')"
                            :rules="[formRules.maxLength(100)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="8">
                        <kp-textfield
                            :model-value="adresse.strasse"
                            @update:model-value="(value) => adresse.set('strasse', value)"
                            dense
                            :label="$t('strasse') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(100)]"
                            @blur="() => adresse.set('strasse', modStreet(adresse.strasse))"
                        ></kp-textfield>
                    </v-col>
                    <v-col cols="4">
                        <kp-textfield
                            :model-value="adresse.strasse_nr"
                            @update:model-value="(value) => adresse.set('strasse_nr', value)"
                            dense
                            :label="$t('nr') + ' *'"
                            :rules="[formRules.required, formRules.maxLength(20)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.postfach"
                            @update:model-value="(value) => adresse.set('postfach', value)"
                            dense
                            :label="$t('postfach')"
                            :rules="[formRules.maxLength(20)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-row wrap align="center">
                    <v-col cols="12" md="6">
                        <PlzOrtSelect
                            dense
                            :plz="adresse.plz"
                            :ort="adresse.ort"
                            @update:plz="(value) => adresse.set('plz', value)"
                            @update:ort="(value) => adresse.set('ort', value)"
                            :rules="[formRules.required]"
                        ></PlzOrtSelect>
                    </v-col>
                    <v-col cols="12" md="6">
                        <LandSelect
                            :model-value="adresse.land_id"
                            @update:model-value="(value) => adresse.set('land_id', value)"
                            dense
                            :label="$t('land')"
                            @landchange="onLandChange"
                        />
                    </v-col>
                </v-row>
                <v-row wrap>
                    <v-col cols="12" md="6">
                        <TelefonField
                            :model-value="adresse.organisation_tel_1"
                            @update:model-value="
                                (value) => adresse.set('organisation_tel_1', value)
                            "
                            dense
                            :label="$t('telefon') + ' 1 *'"
                            :rules="[formRules.required, formRules.maxLength(30)]"
                        ></TelefonField>
                    </v-col>
                    <v-col cols="12" md="6">
                        <TelefonField
                            :model-value="adresse.organisation_tel_2"
                            @update:model-value="
                                (value) => adresse.set('organisation_tel_2', value)
                            "
                            dense
                            :label="$t('telefon') + ' 2'"
                            :rules="[formRules.maxLength(30)]"
                        ></TelefonField>
                    </v-col>
                </v-row>
                <v-row>
                    <v-col cols="12" md="6">
                        <kp-textfield
                            :model-value="adresse.organisation_email"
                            @update:model-value="
                                (value) => adresse.set('organisation_email', value)
                            "
                            dense
                            :label="$t('email') + ' *'"
                            icon-type="email"
                            :disabled="isProfil"
                            :rules="[formRules.required, formRules.email, formRules.maxLength(255)]"
                        ></kp-textfield>
                    </v-col>
                </v-row>
                <v-expansion-panels v-if="gesuchContext" class="ansprechperson-container">
                    <v-expansion-panel elevation="3" class="grey-lighten-2 mb-2">
                        <v-expansion-panel-title v-slot="{ expanded }">
                            <v-row>
                                <v-col cols="12">
                                    <v-card-title v-if="expanded || !adresse.vorname">
                                        <v-icon left>fas fa-user</v-icon>
                                        {{ $t('contact') }}
                                    </v-card-title>
                                    <v-card-title v-else>
                                        <v-icon left>fas fa-user</v-icon>
                                        {{ $t('contact') }}: {{ adresse.vorname }}
                                        {{ adresse.nachname }}
                                    </v-card-title>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-title>
                        <v-expansion-panel-text class="ml-2">
                            <v-row>
                                <v-col cols="12" md="6">
                                    <div class="text-subtitle-1 text-medium-emphasis">
                                        {{ $t('search') }}
                                    </div>
                                    <v-autocomplete
                                        style="margin-left: -1rem"
                                        v-model:model-value="contact"
                                        dense
                                        :loading="contactLoading"
                                        :items="
                                            contactCollection ? contactCollection.getModels() : []
                                        "
                                        cache-items
                                        item-title="vorname_name"
                                        item-value="id"
                                        clearable
                                        return-object
                                        clear-icon="fas fa-times"
                                        @update:search="searchAnsprechPerson"
                                        @update:modelValue="contactSelected"
                                    ></v-autocomplete>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <AnredeSelect
                                        :model-value="adresse.anrede"
                                        @update:model-value="
                                            (value) => adresse.set('anrede', value)
                                        "
                                        dense
                                        :label="$t('anrede')"
                                    />
                                </v-col>
                            </v-row>
                            <v-row wrap>
                                <v-col cols="12" md="6">
                                    <kp-textfield
                                        :model-value="adresse.vorname"
                                        @update:model-value="
                                            (value) => adresse.set('vorname', value)
                                        "
                                        dense
                                        :label="$t('vorname')"
                                        :rules="[formRules.maxLength(100)]"
                                    ></kp-textfield>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <kp-textfield
                                        :model-value="adresse.nachname"
                                        @update:model-value="
                                            (value) => adresse.set('nachname', value)
                                        "
                                        dense
                                        :label="$t('name')"
                                        :rules="[formRules.maxLength(100)]"
                                    ></kp-textfield>
                                </v-col>
                            </v-row>
                            <v-row wrap>
                                <v-col cols="12" md="6">
                                    <TelefonField
                                        :model-value="adresse.tel_1"
                                        @update:model-value="(value) => adresse.set('tel_1', value)"
                                        dense
                                        :label="$t('telefon') + ' 1'"
                                        :rules="[formRules.maxLength(30)]"
                                    ></TelefonField>
                                </v-col>
                                <v-col cols="12" md="6">
                                    <TelefonField
                                        :model-value="adresse.tel_2"
                                        @update:model-value="(value) => adresse.set('tel_2', value)"
                                        dense
                                        :label="$t('telefon') + ' 2'"
                                        :rules="[formRules.maxLength(30)]"
                                    ></TelefonField>
                                </v-col>
                            </v-row>
                            <v-row>
                                <v-col cols="12" md="6">
                                    <kp-textfield
                                        :model-value="adresse.email"
                                        @update:model-value="(value) => adresse.set('email', value)"
                                        dense
                                        icon-type="email"
                                        :label="$t('email')"
                                        :rules="[formRules.email, formRules.maxLength(255)]"
                                    ></kp-textfield>
                                </v-col>
                            </v-row>
                        </v-expansion-panel-text>
                    </v-expansion-panel>
                </v-expansion-panels>

                <v-card
                    elevation="3"
                    :color="customer === 'tg' ? '#40a5364a' : '#ffdc003b'"
                    style="padding: 1rem; margin-left: -1rem; margin-top: 2rem"
                >
                    <div style="font-weight: bold; padding: 10px 5px 5px 5px">
                        {{ $t('nur_wenn_unternehmer_einreicht') }}
                    </div>
                    <v-card-text>
                        <v-row>
                            <v-col cols="12" md="6">
                                <kp-textfield
                                    :model-value="adresse.uid"
                                    @update:model-value="(value) => adresse.set('uid', value)"
                                    :info-text="$t('uid_help')"
                                    dense
                                    :label="$t('uid')"
                                    :rules="[formRules.uid]"
                                ></kp-textfield>
                            </v-col>
                            <v-col cols="12" md="6">
                                <EigentuemerschaftSelect
                                    :model-value="adresse.eigentuemerschaft"
                                    @update:model-value="
                                        (value) => adresse.set('eigentuemerschaft', value)
                                    "
                                    dense
                                    :label="$t('art') + ' *'"
                                />
                            </v-col>
                        </v-row>
                    </v-card-text>
                </v-card>
                <div style="padding: 10px 5px 5px 5px">
                    {{ $t('pflichtfelder_markiert') }}
                </div>
            </v-card-text>
        </v-form>
    </div>
</template>

<style lang="css" scoped>
.ansprechperson-container {
    margin: 2rem 0 0 -1rem;
    width: calc(100% + 1rem);
}
</style>
